/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 * 
 */

import { css, html, TemplateResult, render } from "lit";
import { customElement, property } from "lit/decorators.js";

import { PublicClientApplication, Configuration } from "@azure/msal-browser";

import "@material/mwc-top-app-bar-fixed";
import "@material/mwc-textfield";
import "@material/mwc-button";
import "@material/mwc-icon-button";
import "@material/mwc-drawer";
import "@material/mwc-list";

import "./main-pages/hitl-upload";
import "./main-pages/hitl-review-requests";
import "./main-pages/hitl-review-document";
import "./main-pages/hitl-administration";
import { ReviewRequest } from "./data-classes/review-request";
import { fetchRetry } from "./fetch-retry";
import { Dialog } from "@material/mwc-dialog";
import { Database } from "./database";
import { HitlElement } from "./hitl-element";
import { User } from "./data-classes/user";
import { Client } from "./data-classes/client";
import { Consultant } from "./data-classes/consultant";
import { ConsultantClient } from "./data-classes/consultant-client";

/**
 * to prevent an access token expiration, how many minutes before the expiration should the accessToken be renewed?
 * @param {number} MINUTES_BEFORE_ACCESS_TOKEN_RENEWAL
 */
const MINUTES_BEFORE_ACCESS_TOKEN_RENEWAL = 10;

/**
 * Component types
 */
enum ComponentType {
  Login,
  Upload,
  ManageReviewRequests,
  ReviewDocument,
  Admin,
}

/**
 * Identity types
 */
export type IdentityType = "none" | "gis" | "ad";

/**
 * Central process class
 */
@customElement("hitl-app")
export class HitlApp extends HitlElement {

  /**
   * current component type. The default component type is login
   */
  @property({ type: Number })
  componentType: ComponentType = ComponentType.Login;

  /**
   * Identity type
   */
  @property({ type: String })
  identityType: IdentityType = "none";

  /**
   * Boolean for login is required
   */
  @property({ type: Boolean })
  loginRequired: boolean = false;

  /**
   * Boolean for setup completed
   */
  @property({ type: Boolean })
  setupCompleted: boolean = false;

  /**
   * Remaining time for the session to expire
   */
  @property({ type: String })
  remainingTime!: string;

  /**
   * Timer id for connected- and callback
   */
  private timerId: any ="";

  static override styles = css`
    .dialog-style {
        --mdc-dialog-content-ink-color: #000000;
        color: var(--mdc-dialog-content-ink-color);
        --mdc-shape-medium: 0px;
    }
    .mdc-dialog .mdc-dialog__content {
        color: #000000;
        height: 100px;
        justify-content: center;
        display: flex;
        align-items: center;
    }
    .mdc-dialog .mdc-dialog__surface {
        border-radius: 4px;
        border-color: black;
        border-width: 1px;
        border-style: solid;
    }
    .mdc-dialog__actions {
        display: none;
}`;

  /**
   * Initialize token client
   */
  initTokenClient?: (
    config: google.accounts.oauth2.TokenClientConfig
  ) => google.accounts.oauth2.TokenClient;

  /**
   * Public client application
   */
  ADclientApplication?: PublicClientApplication;
  
  override createRenderRoot() {
    return this;
  }

  /**
   * Access token ad
   */
  #accessTokenAD: string = "";
  /**
   * Access token
   */
  #accessToken: string = "";
  /**
   * Access token expiration
   */
  #accessTokenExpiration: Date = new Date();

  override firstUpdated() {
    // load AI UI component scripts into document head
    let scriptElement = document.createElement("script");
    scriptElement.src =
      "https://aiui.googleapis.com/v1/component/client.js?key=" +
      process.env.API_KEY;
    if (process.env.AI_UI_API_VERSION) {
      scriptElement.src += "&version=" + process.env.AI_UI_API_VERSION;
    }
    document.head.appendChild(scriptElement);
    if (this.identityType === "gis") {
      // load Google Identity Services library and register callbacks to ensure that GIS is loaded before login button is used
      let scriptElement = document.createElement("script");
      scriptElement.src = "https://accounts.google.com/gsi/client";
      scriptElement.onload = this.setupIdentityServices.bind(this);
      document.head.appendChild(scriptElement);
      this.loginRequired = true;
    } else if (this.identityType === "ad") {
      const configuration: Configuration = {
        auth: {
          clientId: process.env.CLIENT_ID!,
          authority: process.env.AD_AUTHORITY,
        },
      };
      this.ADclientApplication = new PublicClientApplication(configuration);
      this.loginRequired = true;
      this.setupCompleted = true;
    } else {
      this.loginRequired = false;
      this.setupCompleted = true;
    }

    /**
     * Drawer for navigation menu
     */
    const drawer = document.getElementsByTagName("mwc-drawer")[0];
    if (drawer) {
      const container = drawer.parentNode;
      if (container) {
        container.addEventListener("MDCTopAppBar:nav", () => {
          drawer.open = !drawer.open;
        });
      }
    }

    const databaseName =
      "projects/" +
      process.env.PROJECT_ID +
      "/instances/" +
      process.env.SPANNER_INSTANCE_NAME +
      "/databases/" +
      process.env.SPANNER_DATABASE_NAME;
    this.database = new Database(databaseName);
    this.dialog = this.renderRoot.querySelector('#dialog') as HTMLDialogElement;
  }

  override connectedCallback(){
    super.connectedCallback();
    //Interval in which timer is updated each 1 sec. = 1000 msec.
      this.timerId = setInterval( async () => {       
        this.remainingTime=""; 
        this.remainingTime = this.getRemainingTimeUntilExpiration()          
        this.requestUpdate();
      }, 1000);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    //Interval for timer gets disconnected after logout
    if(this.timerId)clearInterval(this.timerId);
  }

  override render() {
    const template: TemplateResult[] = [];
    template.push(
      html`
        <mwc-dialog id="dialog"></mwc-dialog>
      `
    );

    //Drawer composition
    template.push(html`
      <mwc-drawer hasHeader type="modal">
        <div>
          <mwc-list>
            <mwc-list-item
              graphic="icon"
              @click=${() => this.changeComponentTo(ComponentType.Upload)}>
              <slot>Hochladen</slot>
              <mwc-icon slot="graphic">upload</mwc-icon>
            </mwc-list-item>
            <mwc-list-item
              graphic="icon"
              @click=${() =>
                this.changeComponentTo(ComponentType.ManageReviewRequests)}>
              <slot>Aufgabenübersicht</slot>
              <mwc-icon slot="graphic">description</mwc-icon>
            </mwc-list-item>
            ${this.users.find((user) => user.userID == this.userID && (user.role == 'ADMIN' || user.role == 'DATA-ADMIN' )) //user has admin or data-admin role
              ? 
              html`
                  <mwc-list-item
                    graphic="icon"
                    @click=${() => this.changeComponentTo(ComponentType.Admin)}>
                    <slot>Administration</slot>
                    <mwc-icon slot="graphic">admin_panel_settings</mwc-icon>
                  </mwc-list-item>
                `
              : null}
                  <mwc-list-item graphic="icon" @click=${this.logout}>
                    <slot>Abmelden</slot>
                    <mwc-icon slot="graphic">logout</mwc-icon>
                  </mwc-list-item>
          </mwc-list>
          <div style="margin: 0px 16px;"><p style="text-align:center;">Verbleibende Sitzungszeit</p></div>
          <div style="border-style: solid; margin: 0px 16px;"><p style="text-align:center;">${this.remainingTime}</p></div>
        </div>
        <div slot="appContent">
          <mwc-top-app-bar-fixed id="header" ?centertitle=${true}>
            ${!this.loginRequired
              ? html`
                  <mwc-icon-button
                    slot="navigationIcon"
                    icon="menu"></mwc-icon-button>
                `
              : html``}
            <div slot="title">
              <span>anydoc.ai</span>
            </div>

            <div slot="actionItems">
              ${this.loginRequired // login required
                ? html`
                    <mwc-button
                      raised
                      style="margin: auto; max-width: 25ex;"
                      @click=${this.login}
                      label="Anmelden"
                      icon="login"
                      ?disabled=${!this.setupCompleted}></mwc-button>
                  `
                : // no login required
                  html`
                    <span
                      style="margin: auto;max-width: 25ex;"
                      id="userID"
                      label="Benutzer ID">
                      ${this.userID}
                    </span>
                  `}
            </div>
          </mwc-top-app-bar-fixed>
        </div>
      </mwc-drawer>
      
    `);
    //Component will be rendered if it is the current one
    switch (this.componentType) {
      //Component to set hitl-upload
      case ComponentType.Upload:
        template.push(
          html`
            <hitl-upload
              .dialog=${this.dialog}
              .database=${this.database}
              .clients=${this.clients}
              .consultants=${this.consultants}
              .filterClients=${this.filterClients}
              .filterConsultants=${this.filterConsultants}
              @get-access-token=${this.getAccessTokenEvent}
              userID=${this.userID}></hitl-upload>
          `
        );
        break;
      //Component to set hitl-review-requests
      case ComponentType.ManageReviewRequests:
        template.push(html`
          <div class="content-wrapper">
            ${!this.userID
              ? html``
              : 
                html`
                  <hitl-review-requests
                    .dialog=${this.dialog}
                    .database=${this.database}
                    .reviewRequests=${this.reviewRequests}
                    @review-requests-updated=${this.addReviewRequests}
                    @get-access-token=${this.getAccessTokenEvent}
                    @batch-review-triggered=${this.startBatchReview}
                  ></hitl-review-requests>
                </div>
                `}
          </div>
        `);
        break;
      //Component to set hitl-review-document
      case ComponentType.ReviewDocument:
        template.push(html`
          <hitl-review-document
            @manage-batch-and-queues-triggered=${this.ManageReviewRequests}
            .reviewRequests=${this.reviewRequests}
            .userID=${this.userID}
            @get-access-token=${this
              .getAccessTokenEvent}></hitl-review-document>
        `);
        break;
      //Component to set hitl-administration
      case ComponentType.Admin:
        template.push(
          html`
            <div class="content-wrapper">
              <hitl-administration
                .userID=${this.userID}
                .dialog=${this.dialog}
                .database=${this.database}
                .users=${this.users}
                .clients=${this.clients}
                .consultants=${this.consultants}                
                @get-access-token=${this.getAccessTokenEvent}
                @clients-changed=${this.clientsChanged}></hitl-administration>
            </div>
          `
        );
        break;
      default:
        //If none of the other components is set this one, also the login is set
        template.push(
          html`
            <login></login>
          `
        );
    }
    template.push(html`
              </main>
            </div>
          </mwc-drawer>
          `);
    return template;
  }

  /**
   * Change current component for another i.e. Upload -> Admin
   * @param component 
   */
  changeComponentTo(component: ComponentType) {
    this.componentType = component;
    const drawer = document.getElementsByTagName("mwc-drawer")[0];
    drawer.open = !drawer;
  }

  /**
   * Setup identity services
   */
  setupIdentityServices() {
    this.setupCompleted = true;
    this.initTokenClient = google.accounts.oauth2.initTokenClient;
  }

  /**
   *  Current method to get accessToken
   */ 
  override getAccessToken = async (refresh = false, scope = "https://www.googleapis.com/auth/cloud-platform email profile", cache = true): Promise<string> => {
    //If cache is true, refresh is false and expiration of the access token is newer than the current date
    if (
      (cache && 
        !refresh &&
        this.#accessToken &&
        this.#accessTokenExpiration > new Date()) ||
    //Or if cache is true, refresh is true and expiration of the access token is newer than the current time     
      (cache && 
        refresh &&
        this.#accessTokenExpiration >
          new Date(
            new Date().getTime() +
              MINUTES_BEFORE_ACCESS_TOKEN_RENEWAL * 60 * 1000
          ))
    ) {
      return this.#accessToken;
    } else if (refresh) {
      // The access token is missing, invalid, or expired, prompt for user consent to obtain one.
      if (this.identityType === "gis") {
        try {
          /**
           * Settle this promise in the response callback for requestAccessToken()
           */
          const promise = new Promise<google.accounts.oauth2.TokenResponse>(
            (resolve, reject) => {
              if (this.initTokenClient) {
                const tokenClient = this.initTokenClient({
                  client_id: process.env.CLIENT_ID!,
                  scope: scope,
                  prompt: "",
                  callback: (
                    tokenResponse: google.accounts.oauth2.TokenResponse
                  ) => {
                    if (tokenResponse.error !== undefined) {
                      console.log("tokenResponse has an error");
                      reject(tokenResponse.error_description);
                    }
                    resolve(tokenResponse);
                  },
                  error_callback: (
                    error: google.accounts.oauth2.ClientConfigError
                  ) => {
                    console.log("error_callback");
                    reject(error);
                  },
                });
                tokenClient.requestAccessToken();
              } else {
                reject("initTokenClient not defined");
              }
            }
          );
          /**
           * Stores token response
           */
          const tokenResponse = await promise;
          if (cache) {
            this.#accessToken = tokenResponse.access_token;
          this.#accessTokenExpiration = new Date(
            new Date().getTime() + parseInt(tokenResponse.expires_in) * 1000
          );
          }     
        } catch (error) {
          console.log("requesting access token failed");
          this.userID = "";
          this.loginRequired = true;
          const dialog = document.getElementById("dialog") as Dialog;
          if (dialog) {
            dialog.innerHTML=""
            dialog.heading = "Anmeldung fehlgeschlagen, aktualisieren Sie bitte die Seite";    //
            render(
              html`
                <mwc-button slot="primaryAction" id="ok" dialogAction="ok">
                  Ok
                </mwc-button>
              `,
              dialog
            );
            const primaryButtonOk = document.getElementById('ok');
            if(primaryButtonOk){
              primaryButtonOk!.addEventListener("click", ()=>{
                  console.log("Seite aktualisiert");
                  location.reload();
              });
            }
              
            dialog.addEventListener('closed', () =>{
                location.reload();
            });
            dialog.open = true;
          }
        }
      //If identity type is ad and ADclientApplication is not null
      } else if (this.identityType === "ad" && this.ADclientApplication) {
        const loginResponse = await this.ADclientApplication.loginPopup({
          scopes: ["openid", "profile", "email"],
        });
        if (loginResponse.idToken) {
          
          /**
           * Call STS (Google's Security Token Service) for token exchange
           */
          const stsUrl = "https://sts.googleapis.com/v1/token";
          const body = JSON.stringify({
            /**
             * Indicates the type of grant being requested
             */
            grantType: "urn:ietf:params:oauth:grant-type:token-exchange",
            /**
             * Specifies the target resource for which the access token is intended
             */
            audience:
              "//iam.googleapis.com/locations/global/workforcePools/" +
              process.env.WORKFORCE_POOL_ID +
              "/providers/" +
              process.env.WORKFORCE_PROVIDER_ID,
            /**
             * Defines the scope of the access token being requested
             */
            scope: "https://www.googleapis.com/auth/cloud-platform",
            /**
             * Indicates the type of token being requested
             */
            requestedTokenType: "urn:ietf:params:oauth:token-type:access_token",
            /**
             * Contains the ID token obtained during the authentication process
             */
            subjectToken: loginResponse.idToken,
            /**
             * Specifies the type of token being exchanged
             */
            subjectTokenType: "urn:ietf:params:oauth:token-type:id_token",
            /**
             * Additional options
             */
            options: JSON.stringify({
              userProject: process.env.WORKFORCE_BILLING_PROJECT_ID,
            }),
          });
          const tokenResponse = await fetch(stsUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: body,
          });
          const tokenResponseJson = await tokenResponse.json();
          if (cache) {
            this.#accessTokenAD = loginResponse.accessToken;
            this.#accessToken = tokenResponseJson.access_token;
            this.#accessTokenExpiration = new Date(
              new Date().getTime() + parseInt(tokenResponseJson.expires_in) * 1000
            );
            console.log("AccessToken expires at", this.#accessTokenExpiration.getMilliseconds())
          }
        }
      }
    } else {
      console.log(
        "cannot fetch access token due to missing user action (e.g. button click)"
      );
      this.userID = "";
      this.loginRequired = true;
      const dialog = document.getElementById("dialog") as Dialog;
      if (dialog) {
        dialog.heading="";
        dialog.innerHTML="";
        dialog.heading = "Sie wurden abgemeldet. Aktualisieren Sie bitte die Seite";    //Anmeldung fehlgeschlagen
        render(
          html`
            <mwc-button slot="primaryAction" dialogAction="ok" @click=${(_: Event) => {location.reload();}}>
              Ok
            </mwc-button>
          `,
          dialog
        );
        dialog.open = true;
      }
    }
    return this.#accessToken;
  };

  /**
   * Login for current user
   */
  login = async () => {
    await this.getAccessToken(true);
    // fetch user info
    let userInfoUrl;
    let accessToken;
    //If identity type is gis then set
    //userInfoUrl is https://www.googleapis.com/oauth2/v1/userinfo
    //Access token is #accessToken
    if (this.identityType === "gis") {
      userInfoUrl = "https://www.googleapis.com/oauth2/v1/userinfo";
      accessToken = this.#accessToken;
    //If identity type is ad then set
    //userInfoUrl is https://graph.microsoft.com/oidc/userinfo
    //Access token is #accessTokenAD
    } else if (this.identityType === "ad") {
      userInfoUrl = "https://graph.microsoft.com/oidc/userinfo";
      accessToken = this.#accessTokenAD;
    }
    //If user info url is not empty then login the user
    if (userInfoUrl) {
      const response = await fetchRetry(
        userInfoUrl,
        {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        },
        5000,
        3
      );
      const json = await response.json();
      console.log(json);
      this.userID = json.email;
    }
    this.loginRequired = false;
    this.initialize();
    this.changeComponentTo(ComponentType.ManageReviewRequests);
  };

  /**
   * Logout user
   */
  logout = async () => {
    this.#accessToken = "";
    this.#accessTokenAD = "";
    this.#accessTokenExpiration = new Date(0);
    this.userID = "";
    this.loginRequired = true;
    const drawer = document.getElementsByTagName("mwc-drawer")[0];
    drawer.open = false;
    location.reload();
  };

  /**
   * Initialize variables
   */
  initialize = async () => {
    if (this.database) {
      const accessToken = await this.getAccessToken();
      this.users = await User.list(this.database, accessToken)
      this.clients = await Client.list(this.database, accessToken);
      this.consultants = await Consultant.list(this.database, accessToken);
      this.consultantClients = await ConsultantClient.list(this.database, accessToken);
      this.filterClients = this.clients;
      this.filterConsultants = this.consultants;
      this.reviewRequests = await ReviewRequest.list(this.database, accessToken); 
    }
  };

  /**
   * Add new review-request
   * @param e 
   */
  async addReviewRequests(e: CustomEvent) {
    const newReviewRequests = e.detail;
    this.reviewRequests = this.reviewRequests.concat(newReviewRequests);
  }

  /**
   * Replace review-requests
   * @param e 
   */
  async replaceReviewRequests(e: CustomEvent) {
    this.reviewRequests = e.detail;
  }

  /**
   * Start batch review
   * @param e 
   */
  startBatchReview(e: CustomEvent) {
    console.log("startBatchReview", e);
    this.reviewRequests = e.detail;
    this.componentType = ComponentType.ReviewDocument;
  }

  /**
   * Component ManageReviewRequest is set and cleanup leftover from DocumentView component
   * @param e 
   */
  ManageReviewRequests(e: CustomEvent) {
    document
      .querySelectorAll(".cdk-overlay-container")
      .forEach((element) => (element.innerHTML = ""));
    this.reviewRequests = e.detail;
    this.componentType = ComponentType.ManageReviewRequests;
  }

  /**
   * Client changed event
   * @param e 
   */
  clientsChanged(e: CustomEvent) {
    console.log("clientsChanged", e);
    this.clients = e.detail;
  }

  /**
   * Gets the reimaining time until the access token expire
   * @returns 
   */
  getRemainingTimeUntilExpiration() {
    const currentTime = new Date();
    const timeUntilExpiration = this.#accessTokenExpiration.getTime() - currentTime.getTime();
    // Convert milliseconds to minutes
    const minutes = Math.floor(timeUntilExpiration / (60 * 1000)); 
    // Convert remaining milliseconds to seconds
    const seconds = Math.floor((timeUntilExpiration % (60 * 1000)) / 1000);
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');
    return `${formattedMinutes}:${formattedSeconds}`;
  }  
}

declare global {
  interface HTMLElementTagNameMap {
    "hitl-app": HitlApp;
  }
}
